import DOMPurify from 'dompurify';
import { emPagination, emLoadMore } from '../../../js/theme/pagination';
import { getParams, setParams } from './params';
import { setActiveFilters } from './active-filters';
import { emFetchPosts, scrollToEl } from '../../../js/theme/helpers';

/**
 * Pass parameters and fetch from post factory api.
 * @param  {Object}   element  the element to append/replace the html.
 * @param  {Object}   params   the query parameters to pass to the rest api.
 * @param  {Function} template the html markup.
 */
const getFilterableContent = (element, params = {}) => {
	(async () => {
		const hasUrlParams = window.location.search;
		if (hasUrlParams) {
			params.term_counts = true;
		} else {
			// Need this for popstate when no url params.
			params.paged = 1;
		}
		// Get the posts
		const data = await emFetchPosts(params);

		if (data.posts) {
			// Get data.
			const {
				blockId,
				posts,
				notFoundMessage,
				foundPosts,
				currentPage,
				pagination,
				terms,
				hasFeatured,
			} = data;

			// Get current block.
			const block = document.getElementById(blockId);

			let html = '';

			// Pass the post data to the template.
			posts.forEach((post, i) => {
				// Sanitize the post content before adding it to HTML
				const sanitizedPost = DOMPurify.sanitize(post);

				// Highlight the first featured post on the first page.
				if (hasFeatured && i === 0 && currentPage === 1) {
					html += `<div class="em-col featured">${sanitizedPost}</div>`;
				} else {
					html += `<div class="em-col">${sanitizedPost}</div>`;
				}
			});

			let paginationHtml = '';

			// Output the pagination.
			if (pagination === 'load_more') {
				const loadMore = block.querySelector('.load-more');
				if (loadMore) {
					const loadMoreParent = loadMore.closest('.em-col-12');
					if (loadMoreParent) {
						loadMoreParent.remove();
					} else {
						loadMore.remove();
					}
				}
				paginationHtml = emLoadMore(data.maxNumPages, currentPage);
			} else {
				paginationHtml = await emPagination(data.maxNumPages, currentPage);
			}

			if (paginationHtml) {
				html += `<div class="em-col-12">${DOMPurify.sanitize(paginationHtml)}</div>`;
			}

			// Append or replace the markup.
			if (pagination === 'load_more' && currentPage !== 1) {
				element.innerHTML += html;
			} else {
				element.innerHTML = html;
			}

			// No Posts Found
			if (posts.length === 0) {
				element.innerHTML = DOMPurify.sanitize(notFoundMessage);
			}

			// Process the pagination actions.
			const pageNumbers = block.querySelectorAll('.page-numbers, .load-more');
			const filterContainer = block.querySelector('.wp-block-embark-filterable-content__filter-container');
			const contentContainer = block.querySelector('.wp-block-embark-filterable-content__listings');
			const inputs = block.querySelectorAll('input');

			// Loop through page numbers.
			pageNumbers.forEach((page) => {
				page.addEventListener('click', (e) => {
					page = e.target.closest('.page-numbers, .load-more');
					filterContainer.setAttribute('data-paged', page.dataset.page || 1);

					if (pagination !== 'load_more') {
						scrollToEl(element.parentElement, 0);
					}

					setParams(block);
					// Fetch the content.
					getFilterableContent(contentContainer, getParams(block));
				});
			});

			// Set Count.
			const resultCount = block.querySelector('.wp-block-embark-filterable-content__count .count');
			if (resultCount) {
				resultCount.innerHTML = DOMPurify.sanitize(foundPosts);
			}

			// Set active filter "pills".
			setActiveFilters(block, inputs, getFilterableContent);

			const updateTermCounts = (terms) => {
				inputs.forEach((input) => {
					const termId = input.getAttribute('data-term-id');
					if (termId) {
						const container = input.closest('.cat-item');
						const count = input.nextElementSibling.querySelector('.count');
						const disable = () => {
							container.classList.add('disabled');
							input.setAttribute('disabled', 'disabled');
							count.innerHTML = '(0)';
						};

						const enable = (countNumber) => {
							container.classList.remove('disabled');
							input.removeAttribute('disabled');
							count.innerHTML = `(${DOMPurify.sanitize(countNumber)})`;
						};

						if (!terms) {
							enable(input.dataset.count);
						} else if (termId in terms) {
							if (terms[termId] === 0) {
								disable();
							} else {
								enable(terms[termId]);
							}
						} else {
							disable();
						}
					}
				});
			};

			//  Update term counts.
			updateTermCounts(terms);

			if (pagination === 'load_more') {
				const links = block.querySelectorAll('a[href]');
				links.forEach((link) => {
					// Respect any server-rendered target attribute.
					if (link.hasAttribute('target')) {
						return;
					}

					try {
						const href = link.getAttribute('href');
						// Skip hash-only links.
						if (!href || href.startsWith('#')) {
							return;
						}

						const url = new URL(href, window.location.origin);
						const linkHost = url.hostname.replace(/^www\./, '');
						const pageHost = window.location.hostname.replace(/^www\./, '');
						const isPdf = url.pathname.toLowerCase().endsWith('.pdf');
						const isExternal = !!linkHost && linkHost !== pageHost;

						// Only force new window for external links or PDFs.
						if (isExternal || isPdf) {
							link.setAttribute('target', '_blank');
							link.setAttribute('rel', 'noopener noreferrer nofollow');
						}
					} catch (e) {
						// Ignore invalid URLs.
					}
				});
			}

			// Scroll to result only if filter is applied
			if (
				hasUrlParams
				&& embark.breakpoints.below('sm')
				&& (
					(pagination === 'load_more' && currentPage === 1)
					|| pagination === 'numbered'
				)
			) {
				scrollToEl(element.parentElement, 50);
			}
		}
	})();
};

export default getFilterableContent;
